import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle, AlertTriangle, XCircle, MapPin, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AttendanceReview({ attendance = [], onApprove }) {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'suspicious':
        return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      default:
        return <XCircle className="h-5 w-5 text-red-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-500">Verified ✓</Badge>;
      case 'suspicious':
        return <Badge className="bg-orange-500">Suspicious ⚠️</Badge>;
      default:
        return <Badge className="bg-red-500">Pending</Badge>;
    }
  };

  const getMatchColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <Card className="p-6 shadow-lg">
      <h3 className="text-lg font-semibold mb-4 flex items-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
        <CheckCircle className="h-5 w-5 mr-2 text-blue-600" />
        Attendance Verification Review
      </h3>

      {attendance.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No attendance records today</p>
      ) : (
        <ScrollArea className="h-[600px]">
          <div className="space-y-4">
            {attendance.map((record) => (
              <div 
                key={record.id} 
                className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2"
                style={{
                  borderColor: record.status === 'verified' ? '#10b981' : 
                               record.status === 'suspicious' ? '#f59e0b' : '#ef4444'
                }}
                data-testid={`attendance-review-${record.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(record.status)}
                    <div>
                      <p className="font-semibold text-gray-800">{record.user_name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(record.check_in_time).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(record.status)}
                </div>

                {/* Dual Authentication Scores */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="bg-white p-3 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <Camera className="h-4 w-4 text-blue-600" />
                      <span className="text-xs text-gray-600">Face Match</span>
                    </div>
                    <p className={`text-2xl font-bold ${getMatchColor(record.face_match_percentage || 0)}`}>
                      {record.face_match_percentage?.toFixed(1) || 0}%
                    </p>
                    <div className="mt-2 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all"
                        style={{ width: `${record.face_match_percentage || 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="bg-white p-3 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span className="text-xs text-gray-600">Location Match</span>
                    </div>
                    <p className={`text-2xl font-bold ${getMatchColor(record.location_match_percentage || 0)}`}>
                      {record.location_match_percentage?.toFixed(1) || 0}%
                    </p>
                    <div className="mt-2 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all"
                        style={{ width: `${record.location_match_percentage || 0}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Location Details */}
                <div className="text-xs text-gray-600 space-y-1">
                  <p>📍 GPS: {record.latitude?.toFixed(4)}, {record.longitude?.toFixed(4)}</p>
                  <p>🏢 Office: {record.office_address || 'Not assigned'}</p>
                  <p>📏 Distance: {record.distance_from_office_km || 0} km</p>
                </div>

                {/* Check-out info if exists */}
                {record.check_out_time && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-600 mb-2">
                      Check-out: {new Date(record.check_out_time).toLocaleString()}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-600">Face: </span>
                        <span className={`font-semibold ${getMatchColor(record.check_out_face_match_percentage || 0)}`}>
                          {record.check_out_face_match_percentage?.toFixed(1) || 0}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Location: </span>
                        <span className={`font-semibold ${getMatchColor(record.check_out_location_match_percentage || 0)}`}>
                          {record.check_out_location_match_percentage?.toFixed(1) || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </Card>
  );
}
